import { selectorAppContext } from '@cancellation-portal/modules/app-context/duck';
import { Card } from '@minna-technologies/minna-ui/components/Card';
import { Body } from '@minna-technologies/minna-ui/components/Typography/Body';
import { IconButton } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import type { FC } from 'react';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import FullscreenContainerView from '@cancellation-portal/shared-components/layout/FullscreenContainerView';
import StickyContainerView from '@cancellation-portal/shared-components/layout/StickyContainerView';
import Visible from '@cancellation-portal/shared-components/layout/Visible';
import { CloseIcon } from '@minna-technologies/minna-ui/icons/Close';

interface SupportDialogProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    textAlign: 'left',
  },
  header: {
    width: '100%',
    background: theme.colors.base.surface,
    borderBottom: '1px solid #e5e7eb',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 12px 0 16px',
  },
  headerTitle: {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: '#515151',
  },
  content: {
    overflow: 'auto',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#F8F8F8',
    padding: '16px',
    minHeight: '300px',
  },
  intro: {
    fontFamily: 'Raleway, sans-serif',
    fontWeight: 700,
    fontSize: '24px',
    lineHeight: '28px',
    color: '#222222',
    marginBottom: '16px',
  },
  section: {
    borderRadius: '6px',
    background: '#fafbfc',
    boxShadow: 'none',
    padding: '16px',
    marginBottom: '12px',
  },
  sectionTitle: {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: 1.4,
    marginBottom: '8px',
  },
  sectionBody: {
    color: '#676561',
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: 1.5,
  },
  footer: {
    marginTop: 'auto',
    paddingTop: '24px',
    width: '100%',
    textAlign: 'center',
    background: '#F8F8F8',
  },
  footerText: {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '16px',
    letterSpacing: '0.3px',
    color: '#222222',
    padding: '0 16px',
  },
  footerButton: {
    display: 'block',
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center',
    padding: '12px 0',
    borderRadius: '64px',
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '16px',
    letterSpacing: '0.3px',
    margin: '12px auto 0',
    cursor: 'pointer',
    border: '1.5px solid rgba(129, 129, 129, 1)',
    color: '#222222CC',
    textDecoration: 'none',
  },
  closeButton: {
    marginRight: 0,
    color: '#02B5B9 !important',
    fontSize: '22px',
  },
}));

export const SupportDialog: FC<SupportDialogProps> = ({ open, onClose }) => {
  const classes = useStyles();
  const appContext = useSelector(selectorAppContext);
  const [mailtoHref, setMailtoHref] = React.useState('');
  const supportEmail = appContext.features.helpCenter.supportEmail;

  const generateMailto = useCallback(() => {
    const randomNumber = Math.floor(10000 + Math.random() * 90000);
    const subject = `CP${randomNumber}`;
    return `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}`;
  }, [supportEmail]);

  const handleMailtoClick = (event: React.MouseEvent) => {
    const href = generateMailto();
    setMailtoHref(href);
    setTimeout(() => {
      window.location.href = href;
    }, 0);
    event.preventDefault();
  };

  useEffect(() => {
    if (open) {
      setMailtoHref(generateMailto());
    }
  }, [open, generateMailto]);

  if (!open) return null;

  const sections = [
    {
      title: 'What to include',
      body: 'Include as much detail as possible so we can help you quickly.',
    },
    {
      title: 'What happens next',
      body: 'Your email will create a support case for our team.',
    },
    {
      title: 'Need urgent help?',
      body: 'Use the email button below and we will route your request.',
    },
  ];

  const content = (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <div className={classes.headerTitle}>Contact support</div>
        <IconButton aria-label="close" onClick={onClose} className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.content}>
        <div className={classes.intro}>If you need help, send us an email and we will create a support ticket.</div>
        {sections.map((section) => (
          <Card key={section.title} className={classes.section}>
            <Body className={classes.sectionTitle}>{section.title}</Body>
            <Body className={classes.sectionBody}>{section.body}</Body>
          </Card>
        ))}
        <div className={classes.footer}>
          <div className={classes.footerText}>
            Send your request to <strong>{supportEmail}</strong>.
          </div>
          <a
            className={classes.footerButton}
            href={mailtoHref}
            onClick={handleMailtoClick}
            role="button"
            tabIndex={0}
            aria-label="Email support"
          >
            Email support
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <Visible cssQuery="only screen and (max-height: 649px), only screen and (max-width: 639px)">
        <FullscreenContainerView isOpen={open} name="support-dialog-mobile">
          {content}
        </FullscreenContainerView>
      </Visible>
      <Visible cssQuery="only screen and (min-width: 640px) and (min-height: 650px)">
        <StickyContainerView isOpen={open} name="support-dialog-desktop">
          {content}
        </StickyContainerView>
      </Visible>
    </div>
  );
};
